import axios from "axios";
import { API_ENDPOINTS, API_BASE_URL } from "../../../../js/api.js";
import { obterTipoProgramaUsuarioLogado } from "../utils/tipoPrograma.js";

/**
 * Serviço de acompanhamentos do usuário logado.
 *
 * Mantém a forma do CadastroEmpresa (uma única rota dedicada) e cai
 * para a rota genérica `tca-empregador` quando a rota dedicada
 * não estiver disponível.
 *
 * Atualizado para consumir:
 *   - GET /tca-empregador/acompanhamentos/me   (rota dedicada nova)
 *     devolve cada item já com etapa_atual/label/cor prontos para o
 *     stepper. Não exige page/limit (quebravam com 500).
 *   - GET /tca-empregador/:id/status-completo  (detalhe individual)
 *     devolve etapa_atual, ultimo_evento, historico JSON completo.
 */

/**
 * Tenta extrair a URL final do endpoint de acompanhamentos dedicado
 * do usuário logado. A PARTIR DE 2026-09-25 a rota oficial é
 * `/acompanhamentos/me` (agregada, multi-programa). A rota legada
 * `/tca-empregador/acompanhamentos/me` continua sendo uma opção de
 * fallback para ambientes que ainda não migraram.
 */
function resolverEndpointAcompanhamentosMe() {
  // Preferência absoluta pela rota nova agregada.
  const epNovo = API_ENDPOINTS?.acompanhamentosMe;
  if (typeof epNovo === "function") return epNovo();
  if (typeof epNovo === "string" && epNovo) return epNovo;

  // Fallback para a rota legada, se cadastrada explicitamente.
  const epLegado = API_ENDPOINTS?.tca_empregador_acompanhamentos_me_legacy;
  if (typeof epLegado === "function") return epLegado();
  if (typeof epLegado === "string" && epLegado) return epLegado;

  return null;
}

/**
 * Tenta extrair a URL do endpoint de status-completo para um id.
 */
function resolverEndpointStatusCompleto(id) {
  const ep = API_ENDPOINTS?.tca_empregador_status_completo;
  if (!ep) return null;
  if (typeof ep === "function") return ep(id);
  // fallback para string com placeholder ":id"
  if (typeof ep === "string") return ep.replace(":id", encodeURIComponent(id));
  return null;
}

export async function listarAcompanhamentosMe({
  token,
  empresaId = null,
  tcaId = null,
  busca = "",
} = {}) {
  if (!token) throw new Error("Token obrigatório");

  // 1. Tenta rota dedicada `/acompanhamentos/me` (agregada, multi-programa
  //    - retornada pelo backend consolidando TCE + TCA + TCA Empregador).
  //    O backend desta API já implementa essa rota e devolve cada item
  //    com etapa_atual/status_label/cor/_origem/tipo_programa derivados.
  const endpointDedicado = resolverEndpointAcompanhamentosMe();
  if (endpointDedicado) {
    try {
      const params = { limit: 200 };
      if (empresaId) params.empresa_id = empresaId;
      if (tcaId) params.tca_empregador_id = tcaId;
      if (busca) params.busca = busca;

      const resposta = await axios.get(endpointDedicado, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      return resposta.data;
    } catch (err) {
      // Em ambiente de homologação anterior à atualização da API, a rota
      // dedicada pode retornar 404. Cai no fallback abaixo.
      if (err?.response?.status && err.response.status !== 404) {
        // Erro diferente de 404 -> propaga.
        if (err.response.status >= 500) {
          // 5xx: fallback silencioso (comportamento antigo preservado).
        } else {
          throw err;
        }
      }
    }
  }

  // 2. Fallback de acordo com o programa do usuário/contexto ativo:
  //    - TCE (tipo 0):            /tce ou /tce/empresa/:empresaId/autorizacao
  //    - TCA Comum (tipo 1):      /tca ou /tca/empresa/:empresaId/autorizacao
  //    - TCA Empregador (tipo 2): /tca-empregador
  // NAO enviamos `page`/`limit` porque o backend atual quebra com 500.
  const tipoProg = obterTipoProgramaUsuarioLogado();
  const params = {};
  if (empresaId) params.empresa_id = empresaId;
  if (tcaId) params.tca_empregador_id = tcaId;
  if (busca) params.busca = busca;

  let urlFallback = `${API_BASE_URL}/tca-empregador`;
  if (tipoProg === 0) {
    urlFallback = empresaId
      ? `${API_BASE_URL}/tce/empresa/${empresaId}/autorizacao`
      : `${API_BASE_URL}/tce`;
  } else if (tipoProg === 1) {
    urlFallback = empresaId
      ? `${API_BASE_URL}/tca/empresa/${empresaId}/autorizacao`
      : `${API_BASE_URL}/tca`;
  }

  const resposta = await axios.get(urlFallback, {
    params,
    headers: { Authorization: `Bearer ${token}` },
  });
  return resposta.data;
}

export async function listarAcompanhamentosFallback({
  token,
  busca,
  empresaId,
  tcaId,
} = {}) {
  if (!token) throw new Error("Token obrigatório");
  const params = {};
  if (empresaId) params.empresa_id = empresaId;
  if (tcaId) params.tca_empregador_id = tcaId;
  if (busca) params.busca = busca;

  const tipoProg = obterTipoProgramaUsuarioLogado();
  let url = `${API_BASE_URL}/tca-empregador`;
  if (tipoProg === 0) {
    url = empresaId
      ? `${API_BASE_URL}/tce/empresa/${empresaId}/autorizacao`
      : `${API_BASE_URL}/tce`;
  } else if (tipoProg === 1) {
    url = empresaId
      ? `${API_BASE_URL}/tca/empresa/${empresaId}/autorizacao`
      : `${API_BASE_URL}/tca`;
  }

  const resposta = await axios.get(url, {
    params,
    headers: { Authorization: `Bearer ${token}` },
  });
  return resposta.data;
}


/**
 * Busca o status completo (etapa derivada, último evento e histórico JSON)
 * de UM cadastro específico. Substitui a necessidade de o frontend
 * adivinhar o status textual.
 *
 * Quando o backend ainda não implementou a rota /status-completo, devolve
 * `null` silenciosamente (o caller cai no fluxo antigo via `STATUS_PARA_ETAPA`).
 *
 * @param {{ token: string, id: number|string, signal?: AbortSignal }} args
 * @returns {Promise<object|null>}
 */
export async function buscarStatusCompletoDaEmpresa({ token, id, signal } = {}) {
  if (!token || id == null) return null;

  const endpoint = resolverEndpointStatusCompleto(id);
  if (!endpoint) return null;

  try {
    const resposta = await axios.get(endpoint, {
      headers: { Authorization: `Bearer ${token}` },
      // Não cachear: o status pode mudar a cada aprovação/reprovação.
      params: { _: Date.now() },
      signal,
    });
    return resposta.data;
  } catch (err) {
    // Cancelamento explícito (AbortController) → propaga pra cima.
    if (axios.isCancel(err)) throw err;
    // 404 (rota não disponível) → fallback silencioso.
    // Outros erros: propaga para a UI mostrar mensagem apropriada.
    if (err?.response?.status === 404) return null;
    throw err;
  }
}
